import { Product, SingleSupplierCart } from 'types/schema';
import isGoogleBot from './urls/isGoogleBot';

declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
  }
}

export enum EventName {
  BEGIN_CHECKOUT = 'begin_checkout',
  ADD_SHIPPING_INFO = 'add_shipping_info',
  ADD_PAYMENT_INFO = 'add_payment_info',
  PURCHASE = 'purchase',
  VIEW_ITEM = 'view_item',
  ADD_TO_CART = 'add_to_cart',
  SIGN_UP = 'sign_up',
  LOGIN = 'login',
  VIEW_ORDER_HISTORY_PAGE = 'view_order_history_page',
  VIEW_DELIVERY_DETAIL_PAGE = 'view_delivery_detail_page',
  CLICK_RATING_BUTTON = 'click_rating_button',
  CLICK_LATER_RATING = 'click_later_rating',
  SUBMIT_RATING = 'submit_rating',
  SUBMIT_RATING_ERROR = 'submit_rating_error',
  VIEW_FARM_TO_BIZ_PAGE_FTB = 'view_farm_to_biz_page_ftb',
  CLICK_FIND_SUPPLIER_FTB = 'click_find_supplier_ftb',
  CLICK_WATCH_VIDEO_FTB = 'click_watch_video_ftb',
  CLICK_PRODUCT_ITEM_FTB = 'click_product_item_ftb',
  ADD_TO_CART_FROM_FARM_FTB = 'add_to_cart_from_farm_ftb',
  SCROLL_PERCENTAGE_FTB = 'scroll_percentage_ftb',
  CLICK_REORDER_FAVORITE_FTB = 'click_reorder_favorite_ftb',
  CLICK_KAMEREO_BRAND_FTB = 'click_kamereo_brand_ftb',
  CLICK_FARM_IMAGE_FTB = 'click_farm_image_ftb',
  START_SOURCING_FORM_SUBMIT_FTB = 'start_sourcing_form_submit_ftb',
  START_SOURCING_FORM_FIELD_INPUT_FTB = 'start_sourcing_form_field_input_ftb',
  GET_PRODUCT_CONSULTATION_FORM_SUBMIT_FTB = 'get_product_consultation_form_submit_ftb',
  GET_PRODUCT_CONSULTATION_FORM_FIELD_INPUT_FTB = 'consultation_form_field_input_ftb',
  START_SOURCING_FTB_CLICK = 'start_sourcing_ftb_click',
  PRODUCT_CONSULT_FTB_CLICK = 'product_consult_ftb_click',
  KMR_PB_CLICK = 'kmr_pb_click',

  // Product detail page (PDP) events
  PDP_VIEW_ITEM = 'pdp_view_item',
  PDP_ADD_TO_CART = 'pdp_add_to_cart',
  PDP_IMAGE_INTERACTION = 'pdp_image_interaction',
  PDP_SCROLL_DEPTH = 'pdp_scroll_depth',
  PDP_MINI_BANNER_CLICK = 'pdp_mini_banner_click',
  PDP_RECOMMENDATION_CLICK = 'pdp_recommendation_click',
  PDP_VIEW_REVIEW_SECTION = 'pdp_view_review_section',
  PDP_REVIEW_SUBMIT = 'pdp_review_submit',
  PDP_ADD_TO_FAVORITE = 'pdp_add_to_favorite',
  PDP_CLICK_SHIPPING_POLICY = 'pdp_click_shipping_policy',
  PDP_CLICK_ACTUAL_WEIGHT = 'pdp_click_actual_weight',
  PDP_CLICK_BRAND = 'pdp_click_brand',
  PDP_CLICK_VIEW_MORE = 'pdp_click_view_more',
  PDP_CLICK_PRIVATE_LABEL = 'pdp_click_private_label',
  PDP_CLICK_DESCRIPTION = 'pdp_click_description',
  PDP_CLICK_SUGGEST_BOX = 'pdp_click_suggest_box',

  // Search bar events
  SEARCH_FOCUS = 'search_focus',
  SEARCH_TYPING = 'search_typing',
  SEARCH_SUGGESTION_CLICK = 'search_suggestion_click',
  SEARCH_SUBMIT = 'search_submit',
  SEARCH_CLEAR = 'search_clear',

  // Search results page events
  VIEW_SEARCH_RESULTS = 'view_search_results',
  NO_RESULT_VIEW = 'no_result_view',
  PRODUCT_CLICK_FROM_SEARCH = 'product_click_from_search',
  ADD_TO_CART_FROM_SEARCH = 'add_to_cart_from_search',
  SEARCH_PAGE_FILTER_APPLY = 'search_page_filter_apply',
  SEARCH_PAGE_SORT_APPLY = 'search_page_sort_apply',
  SEARCH_PAGE_PAGINATION_CLICK = 'search_page_pagination_click',
  SEARCH_PAGE_SEARCH_LATENCY = 'search_page_search_latency',

  // Quick order
  QUICK_ORDER_ANALYZE_CLICK = 'quick_order_analyze_click',
  QUICK_ORDER_ANALYZE_AGAIN_CLICK = 'quick_order_analyze_again_click',
  QUICK_ORDER_ADD_TO_CART_CLICK = 'quick_order_add_to_cart_click',

  //Referral Program
  REFERRAL_PROGRAM_CLICK_COPY = 'referral_program_click_copy',
  REFERRAL_PROGRAM_CLICK_SHARE = 'referral_program_click_share',
  REFERRAL_PROGRAM_CLICK_HUB = 'referral_program_click_hub',
  REFERRAL_PROGRAM_CLICK_LANDING_CTA = 'referral_program_click_landing_cta',
}

interface IOrderItem {
  item_id: string;
  item_name: string;
  index: number;
  item_category?: string;
  item_category2?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_brand: string;
  price: number;
  quantity: number;
}
interface IEcommerceData {
  currency: string;
  value: string | number;
  items: IOrderItem[];
  shipping_tier?: string;
  payment_type?: string;
  transaction_id?: string;
}
interface IProduct extends Product {
  quantity?: number;
  addFrom?: string;
}
interface ICart extends SingleSupplierCart {
  id?: string;
}
type DataParams = ICart | IProduct;

interface IDataLayer extends Record<string, any> {
  event?: string;
  ecommerce?: IEcommerceData | null;
}

const sendDataLayer = (dataLayer: IDataLayer) => {
  // if (isGoogleBot() || process.env.APP_ENV !== 'production') return;
  if (typeof window === 'undefined') return;

  // GTM is injected via a script tag at the end of <body> in index.hbs, so
  // window.dataLayer may not exist yet when the React app fires an early
  // event. Initialise it ourselves and push directly: GTM replays the whole
  // window.dataLayer array once its container loads, so events pushed before
  // init are not lost.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataLayer);
};
const generateScreenID = (screenName: string) => {
  const screenNameArr = screenName.split(/(?=[A-Z])/);
  return screenNameArr.map((word) => word.toLowerCase()).join('_');
};

const handleGenerateOrderListData = (data: ICart) => {
  return data?.items.map((itemCart, index) => {
    const { product, quantity } = itemCart;
    const result: IOrderItem = {
      item_id: product?.id ?? '',
      item_name: product?.name ?? '',
      index: index,
      item_brand: 'KAMEREO',
      price: product?.price ?? 0,
      quantity: quantity ?? 0,
    };
    if (product?.category) {
      result.item_category = product.category?.parent?.name;
      result.item_category2 = product.category?.name;
    }
    return result;
  });
};
const handleGenerateSingleItem = (data: IProduct) => {
  const result: IOrderItem = {
    item_id: data?.id ?? '',
    item_name: data?.name ?? '',
    index: 0,
    item_category: data.category?.parent?.name ?? '',
    item_category2: data.category?.name ?? '',
    item_brand: 'KAMEREO',
    price: data?.price ?? 0,
    quantity: data.quantity ?? 1,
  };
  if (data.addFrom) {
    result.item_list_id = generateScreenID(data.addFrom);
    result.item_list_name = data.addFrom;
  }
  return [result];
};
const isCheckoutJourney = (data: DataParams): data is ICart => 'items' in data;

const triggerGA4EcommerceEvent = (eventName: EventName, data: DataParams) => {
  if (isGoogleBot() || process.env.APP_ENV !== 'production') return;
  const ecommerceData: IEcommerceData = {
    currency: 'VND',
    value: 0,
    items: [],
  };
  if (isCheckoutJourney(data)) {
    ecommerceData.items = handleGenerateOrderListData(data);
    ecommerceData.value = data.total ?? 0;
    switch (eventName) {
      case EventName.ADD_SHIPPING_INFO:
        ecommerceData.shipping_tier = 'GROUND';
        break;
      case EventName.ADD_PAYMENT_INFO:
        ecommerceData.payment_type = 'COD';
        break;
      case EventName.PURCHASE:
        ecommerceData.transaction_id = data.id;
        break;
      default:
        break;
    }
  } else {
    ecommerceData.items = handleGenerateSingleItem(data);
    switch (eventName) {
      case EventName.VIEW_ITEM:
        ecommerceData.value = data.price;
        break;
      case EventName.ADD_TO_CART:
        ecommerceData.value = data.price * (data?.quantity ?? 1);
        break;
      default:
        break;
    }
  }
  sendDataLayer({ ecommerce: null }); // Clear the previous ecommerce object.
  sendDataLayer({
    event: eventName,
    ecommerce: ecommerceData,
  });
};

export const triggerGA4SignUpEvent = (data) => {
  sendDataLayer({
    event: EventName.SIGN_UP,
    ...data,
  });
};

export const triggerGA4SendUserIdEvent = (userId: string) => {
  sendDataLayer({
    user_id: userId,
  });
};

const seenGA4ParamKeys = new Set<string>();

export const sendGA4Event = (eventName: EventName, data?: Record<string, any>) => {
  if (seenGA4ParamKeys.size > 0) {
    const resetPayload: Record<string, undefined> = {};
    seenGA4ParamKeys.forEach((key) => {
      resetPayload[key] = undefined;
    });
    sendDataLayer(resetPayload);
  }

  if (data) {
    Object.keys(data).forEach((key) => seenGA4ParamKeys.add(key));
  }

  sendDataLayer({
    event: eventName,
    ...data,
  });
};

export default triggerGA4EcommerceEvent;
